import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Users, Check, X, LogIn } from "lucide-react";

export default function CollabAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [space, setSpace] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [nameSubmitted, setNameSubmitted] = useState(false);

  const submitAccept = (name = "") => {
    setStatus("accepting");
    const nameQ = name ? `&display_name=${encodeURIComponent(name)}` : "";
    api.post(`/collab/accept?token=${encodeURIComponent(token)}${nameQ}`)
      .then((r) => { setSpace(r.data); setStatus("done"); })
      .catch((e) => {
        setError(e?.response?.data?.detail || e?.message || "accept_failed");
        setStatus("error");
      });
  };

  useEffect(() => {
    if (loading || !token) return;
    if (!user) return;
    if (!nameSubmitted) return;      // wait for name gate
    submitAccept(displayName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, token, nameSubmitted]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center border border-rose-400/60 bg-rose-500/10 p-6">
          <X size={20} className="mx-auto text-rose-300" />
          <div className="mt-2 text-[13px] font-mono uppercase tracking-wider text-rose-200">
            MISSING_INVITE_TOKEN
          </div>
        </div>
      </div>
    );
  }

  if (!loading && !user) {
    return (
      <div data-testid="collab-accept-signin" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full border border-amber-400/60 bg-[#0E0E0E] p-6">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-amber-300 flex items-center gap-2">
            <Users size={12} /> COLLAB_INVITE
          </div>
          <h1 className="font-mono text-xl mt-2">Sign in to accept</h1>
          <p className="text-[12px] font-mono text-zinc-400 mt-2">
            You need to be signed in with the email address the sender invited.
          </p>
          <Link
            to={`/login?next=${encodeURIComponent(`/collab/accept?token=${token}`)}`}
            data-testid="collab-accept-login-cta"
            className="mt-4 h-10 px-4 inline-flex items-center gap-2 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[11px] tracking-wider"
          >
            <LogIn size={12} /> SIGN_IN
          </Link>
          <Link
            to={`/register?next=${encodeURIComponent(`/collab/accept?token=${token}`)}`}
            className="ml-2 h-10 px-4 inline-flex items-center gap-2 border border-purple-400 text-purple-200 hover:bg-purple-400 hover:text-black font-mono uppercase text-[11px] tracking-wider"
          >
            REGISTER
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="collab-accept-page" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full border border-emerald-400/60 bg-[#0E0E0E] p-6 text-center">
        {status === "accepting" && (
          <div className="text-[12px] font-mono text-zinc-400">ACCEPTING_INVITE…</div>
        )}
        {status === "done" && (
          <>
            <Check size={24} className="mx-auto text-emerald-300" />
            <div className="mt-2 text-[13px] font-mono uppercase tracking-wider text-emerald-200">
              JOINED · {space?.name}
            </div>
            <button
              data-testid="collab-accept-goto"
              onClick={() => nav(`/collab?space=${space?.id}`)}
              className="mt-4 h-10 px-4 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300 font-mono uppercase text-[11px] tracking-wider"
            >
              OPEN_WORKSPACE
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <X size={20} className="mx-auto text-rose-300" />
            <div className="mt-2 text-[13px] font-mono uppercase tracking-wider text-rose-200">
              {error}
            </div>
            <Link to="/collab" className="mt-4 inline-block text-[11px] font-mono text-zinc-400 hover:text-white">
              GO_TO_COLLAB →
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
